import {
  HOUSEKEEPING_NOTE_MAX_LENGTH,
  HOUSEKEEPING_TASK_KINDS,
  HOUSEKEEPING_TASK_SOURCES,
  HOUSEKEEPING_TASK_STATUSES,
  MAX_SCHEDULE_TIMES,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';

// Housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md §2.3). Nothing here names a
// patient: bed tasks carry the ward, the bed and, for a terminal clean, the precaution words.

const nameSchema = z.string().trim().min(1).max(80);

export const housekeepingScheduleSchema = z.object({
  id: z.uuid(),
  areaId: z.uuid(),
  taskName: z.string(),
  times: z.array(z.string()),
  active: z.boolean(),
});
export type HousekeepingScheduleView = z.infer<typeof housekeepingScheduleSchema>;

export const housekeepingAreaSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  active: z.boolean(),
  schedules: z.array(housekeepingScheduleSchema),
});
export type HousekeepingAreaView = z.infer<typeof housekeepingAreaSchema>;

export const housekeepingAreasSchema = z.object({ areas: z.array(housekeepingAreaSchema) });
export type HousekeepingAreasView = z.infer<typeof housekeepingAreasSchema>;

export const saveAreaRequestSchema = z.object({ name: nameSchema, active: z.boolean() });
export type SaveAreaRequest = z.infer<typeof saveAreaRequestSchema>;

export const saveScheduleRequestSchema = z.object({
  taskName: nameSchema,
  times: z.array(z.string().trim().max(5)).min(1).max(MAX_SCHEDULE_TIMES),
  active: z.boolean(),
});
export type SaveScheduleRequest = z.infer<typeof saveScheduleRequestSchema>;

export const housekeepingConsumableSchema = z.object({
  id: z.uuid(),
  itemName: z.string(),
  storeName: z.string(),
  batchNumber: z.string(),
  quantity: z.number().int(),
  unit: z.string(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
});

export const housekeepingTaskSchema = z.object({
  id: z.uuid(),
  kind: z.enum(HOUSEKEEPING_TASK_KINDS),
  title: z.string(),
  area: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  ward: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  bed: z.object({ id: z.uuid(), label: z.string() }).nullable(),
  precautions: z.array(z.string()),
  source: z.enum(HOUSEKEEPING_TASK_SOURCES),
  dueAt: z.iso.datetime(),
  status: z.enum(HOUSEKEEPING_TASK_STATUSES),
  overdue: z.boolean(),
  startedAt: z.iso.datetime().nullable(),
  startedBy: actorSchema.nullable(),
  completedAt: z.iso.datetime().nullable(),
  completedBy: actorSchema.nullable(),
  note: z.string().nullable(),
  statusReason: z.string().nullable(),
  consumables: z.array(housekeepingConsumableSchema),
});
export type HousekeepingTaskView = z.infer<typeof housekeepingTaskSchema>;

export const housekeepingTaskListSchema = z.object({ items: z.array(housekeepingTaskSchema) });
export type HousekeepingTaskListView = z.infer<typeof housekeepingTaskListSchema>;

export const areaTasksQuerySchema = z.object({ areaId: z.uuid(), date: isoDaySchema });

export const completeTaskRequestSchema = z.object({
  note: z.string().trim().max(HOUSEKEEPING_NOTE_MAX_LENGTH).nullable(),
});
export type CompleteTaskRequest = z.infer<typeof completeTaskRequestSchema>;

export const skipTaskRequestSchema = z.object({ reason: reasonSchema });

export const consumableOptionsSchema = z.object({
  stores: z.array(z.object({ id: z.uuid(), name: z.string() })),
  items: z.array(
    z.object({
      itemId: z.uuid(),
      name: z.string(),
      unit: z.string(),
      available: z.number().int(),
    }),
  ),
});
export type ConsumableOptionsView = z.infer<typeof consumableOptionsSchema>;

export const addConsumableRequestSchema = z
  .object({
    storeId: z.uuid(),
    itemId: z.uuid(),
    quantity: z.number().int().min(1).max(100_000),
    /** A batch other than the earliest-expiry one, with a reason (FEFO, domain rule 3). */
    batchId: z.uuid().nullable(),
    batchOverrideReason: reasonSchema.nullable(),
  })
  .refine((v) => v.batchId === null || v.batchOverrideReason !== null, {
    message: 'Choosing a batch needs a reason',
    path: ['batchOverrideReason'],
  });
export type AddConsumableRequest = z.infer<typeof addConsumableRequestSchema>;
